/*
 * VEEZE collection filters
 * ========================
 *
 * Progressive enhancement over a plain GET form, which is what Shopify's
 * storefront filtering already is: the checkboxes are named `filter.p.tag`,
 * `filter.v.availability` and so on, so submitting the form navigates to the
 * filtered collection URL with no JavaScript involved at all. The submit button
 * ships in the markup for exactly that case.
 *
 * This element does three things on top of it:
 *
 *   1. submits on change, and removes the now-redundant button
 *   2. drops `page` from the query so applying a filter returns to page 1
 *   3. keeps the price slider's fill and its "bis 45 €" label in step with the
 *      knob, and mirrors the knob into the numeric input Shopify filters on
 *
 * No section-rendering API and no fetch: a filter change is a real navigation,
 * so the back button, deep links and shared URLs all behave.
 */

class VeezeFilters extends HTMLElement {
  #form = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    // Marks the element as script-driven; the stylesheet hides the submit button
    // off the back of it, so the button is only ever hidden once it is redundant.
    this.setAttribute('data-enhanced', '');

    this.#form.addEventListener('change', this.#onChange);
    this.#form.addEventListener('input', this.#onInput);

    for (const range of this.#ranges()) this.#paint(range);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('change', this.#onChange);
    this.#form?.removeEventListener('input', this.#onInput);
  }

  #ranges() {
    return this.querySelectorAll('.veeze-fp__range');
  }

  #onInput = (event) => {
    const range = event.target.closest?.('.veeze-fp__range');
    if (range) this.#paint(range);
  };

  #onChange = (event) => {
    const range = event.target.closest?.('.veeze-fp__range');
    if (range) this.#paint(range);
    this.#submit();
  };

  /*
   * The knob is the visible control; the field named `filter.v.price.lte` is what
   * actually gets submitted. Painting keeps the fill, the label and that field in
   * agreement, so whichever of them the browser restores on a back-navigation the
   * three still match.
   */
  #paint(range) {
    const min = Number(range.min || 0);
    const max = Number(range.max || 0);
    const value = Number(range.value || 0);
    const span = max - min;
    const fill = span > 0 ? ((value - min) / span) * 100 : 100;

    range.style.setProperty('--veeze-fp-fill', `${fill}%`);

    const panel = range.closest('.veeze-fp');
    if (!panel) return;

    const field = panel.querySelector('[data-price-field]');
    if (field) field.value = String(value);

    const output = panel.querySelector('[data-price-output]');
    if (output) {
      const prefix = output.dataset.prefix ? `${output.dataset.prefix} ` : '';
      const suffix = output.dataset.suffix ? ` ${output.dataset.suffix}` : '';
      output.textContent = `${prefix}${value}${suffix}`;
    }
  }

  #submit() {
    const url = new URL(this.#form.action, window.location.href);
    const params = new URLSearchParams(new FormData(this.#form));

    // An empty value would otherwise become `?filter.v.price.lte=`, which reads
    // as a filter of zero rather than as no filter.
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key);
    }

    // Page 1 of the new result set, not page 4 of the old one.
    params.delete('page');

    const query = params.toString();
    window.location.assign(query ? `${url.pathname}?${query}` : url.pathname);
  }
}

if (!customElements.get('veeze-filters')) {
  customElements.define('veeze-filters', VeezeFilters);
}

/*
 * The mobile bar and the sheet it opens.
 *
 * The sheet is a <dialog open> in the markup so it still works with no
 * JavaScript — it reads as a panel below the bar and its form submits normally.
 * This closes it on connect and reopens it with showModal(), which is the only
 * way to get the top layer and a ::backdrop: a non-modal dialog has neither, a
 * lesson this theme's cart drawer already paid for.
 *
 * Above the breakpoint the sheet stays closed and hidden — the sidebar column is
 * the presentation there — and crossing the breakpoint with the sheet open closes
 * it, so it cannot be left stranded off-screen.
 */
class VeezeFilterBar extends HTMLElement {
  #sheet = null;
  #query = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    this.#sheet = this.querySelector('[data-sheet]');
    if (!this.#sheet) return;

    // Only now that the script is running does the inline panel become a dialog.
    if (this.#sheet.open) this.#sheet.close();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onSortChange);
    this.#sheet.addEventListener('cancel', this.#onCancel);
    this.#query.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onSortChange);
    this.#sheet?.removeEventListener('cancel', this.#onCancel);
    this.#query.removeEventListener('change', this.#onBreakpoint);
  }

  /*
   * A <select> inside a form does not submit on its own, and the bar has no
   * visible submit button — the whole control is the picker. So a change to the
   * sort order sends the form, which already carries the active filters as hidden
   * fields so sorting never silently clears them.
   */
  #onSortChange = (event) => {
    const select = event.target.closest('[data-sort-form] select');
    if (select) select.form?.submit();
  };

  #onClick = (event) => {
    if (event.target.closest('[data-open-sheet]')) {
      event.preventDefault();
      this.#open();
      return;
    }
    if (event.target.closest('[data-close-sheet]')) {
      event.preventDefault();
      this.#close();
    }
  };

  #onCancel = () => {
    // Escape: let the dialog close itself, but put the scroll lock back.
    this.#unlock();
  };

  #onBreakpoint = (event) => {
    if (!event.matches) this.#close();
  };

  #open() {
    if (this.#sheet.open) return;
    this.#sheet.showModal();
    this.#lock();
  }

  #close() {
    if (this.#sheet.open) this.#sheet.close();
    this.#unlock();
  }

  /*
   * Locking the page behind a modal has to reserve the scrollbar's width, or the
   * content jumps sideways as it disappears. `scrollbar-gutter` is scoped to the
   * locked state for the same reason: left on permanently it paints a strip of
   * page background down the edge.
   */
  #lock() {
    document.documentElement.setAttribute('data-veeze-filter-open', '');
  }

  #unlock() {
    document.documentElement.removeAttribute('data-veeze-filter-open');
  }
}

if (!customElements.get('veeze-filter-bar')) {
  customElements.define('veeze-filter-bar', VeezeFilterBar);
}
